import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import { recentVisitsService } from '../services/api';
import { isCatalogProductForDisplay, isDemoProduct } from '../utils/isDemoProduct';

function getUserStorageId(user) {
  if (!user) return null;
  return user.id ?? user._id ?? null;
}

function hasAuthToken() {
  return !!localStorage.getItem('authToken');
}

/** Recent product views are kept for this long, then dropped from history (localStorage). */
const RECENT_VISIT_RETENTION_MS = 15 * 24 * 60 * 60 * 1000;
/** Min interval between POST recent-visits for the same product (per browser tab). */
const RECENT_VISIT_POST_THROTTLE_MS = 5 * 60 * 1000;

function pruneStaleRecentVisits(visits) {
  if (!Array.isArray(visits) || visits.length === 0) return visits;
  const cutoff = Date.now() - RECENT_VISIT_RETENTION_MS;
  const next = visits.filter((item) => {
    if (!isCatalogProductForDisplay(item)) return false;
    const ts = item?.visitedAt ? Date.parse(item.visitedAt) : NaN;
    if (Number.isNaN(ts)) return false;
    return ts >= cutoff;
  });
  return next.length === visits.length ? visits : next;
}

function mapApiVisitToVisitItem(row) {
  const product = row?.product && typeof row.product === 'object' ? row.product : null;
  const productId =
    product?._id ||
    product?.id ||
    row?.productId ||
    row?.product?._id ||
    row?.id ||
    row?._id;
  if (productId == null || String(productId).trim() === '') return null;
  const id = String(productId);
  const name = product?.name || row?.productName || row?.name || 'Product';
  const primary = product ? getPrimaryProductImageUrl(product) : null;
  let image = primary;
  if (!image && row?.image) {
    image = typeof row.image === 'string' ? row.image : row.image?.url;
  }
  const visitItem = {
    id,
    _id: id,
    name,
    price: product?.price ?? row?.price ?? 0,
    originalPrice: product?.originalPrice ?? row?.originalPrice ?? product?.price ?? row?.price ?? 0,
    image,
    category: product?.category || row?.category,
    subcategory: product?.subcategory || row?.subcategory,
    url: product?.url || row?.url,
    isNew: product?.isNew ?? row?.isNew,
    isOnSale: product?.isOnSale ?? row?.isOnSale,
    visitedAt: row?.visitedAt || row?.viewedAt || row?.lastVisitedAt || row?.createdAt || new Date().toISOString(),
    visits: row?.visits ?? row?.visitCount ?? 1,
  };
  return isCatalogProductForDisplay(visitItem) ? visitItem : null;
}

function visitsArrayFromApiResponse(response) {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const list = data.visits ?? data.items ?? data.recentVisits ?? data.history;
    if (Array.isArray(list)) return list;
  }
  if (Array.isArray(response?.visits)) return response.visits;
  return null;
}

function shouldThrottleRecentVisitPost(productId) {
  const key = `recent_visit_post_${productId}`;
  try {
    const raw = sessionStorage.getItem(key);
    const last = raw ? Number(raw) : 0;
    const now = Date.now();
    if (last && now - last < RECENT_VISIT_POST_THROTTLE_MS) return true;
    sessionStorage.setItem(key, String(now));
  } catch {
    /* ignore */
  }
  return false;
}

const TrackingContext = createContext();

export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
};

export const TrackingProvider = ({ children }) => {
  const { user } = useAuth();
  const [recentVisits, setRecentVisits] = useState([]);
  const [likedItems, setLikedItems] = useState([]);
  const [orderedItems, setOrderedItems] = useState([]);
  const recentVisitsRef = useRef([]);
  const userStorageId = getUserStorageId(user);

  useEffect(() => {
    recentVisitsRef.current = recentVisits;
  }, [recentVisits]);

  const mergeVisitLists = useCallback((localList, backendList) => {
    const byId = new Map();
    const add = (item) => {
      if (!item?.id) return;
      const id = String(item.id);
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, item);
        return;
      }
      const existingTs = Date.parse(existing.visitedAt || 0);
      const newTs = Date.parse(item.visitedAt || 0);
      const visits = Math.max(existing.visits || 1, item.visits || 1);
      if (newTs >= existingTs) {
        byId.set(id, { ...item, visits });
      } else {
        byId.set(id, { ...existing, visits });
      }
    };
    (backendList || []).forEach(add);
    (localList || []).forEach(add);
    return pruneStaleRecentVisits(
      Array.from(byId.values()).sort(
        (a, b) => Date.parse(b.visitedAt || 0) - Date.parse(a.visitedAt || 0)
      )
    ).slice(0, 50);
  }, []);

  const loadRecentVisitsFromBackend = useCallback(async () => {
    if (!user || !hasAuthToken()) return;
    try {
      const response = await recentVisitsService.getRecentVisits();
      const rawList = visitsArrayFromApiResponse(response);
      if (Array.isArray(rawList)) {
        const backendItems = rawList.map(mapApiVisitToVisitItem).filter(Boolean);
        const merged = mergeVisitLists(recentVisitsRef.current, backendItems);
        setRecentVisits(merged);
      }
    } catch (err) {
      console.warn('Recent visits GET failed (backend may not expose this yet):', err?.message || err);
    }
  }, [user, mergeVisitLists]);

  useEffect(() => {
    if (!userStorageId) {
      setRecentVisits([]);
      setLikedItems([]);
      setOrderedItems([]);
      return;
    }

    const savedVisits = localStorage.getItem(`recent_visits_${userStorageId}`);
    const savedLikes = localStorage.getItem(`liked_items_${userStorageId}`);
    const savedOrders = localStorage.getItem(`ordered_items_${userStorageId}`);

    if (savedVisits) {
      try {
        const parsed = JSON.parse(savedVisits);
        setRecentVisits(pruneStaleRecentVisits(Array.isArray(parsed) ? parsed : []));
      } catch {
        setRecentVisits([]);
      }
    } else {
      setRecentVisits([]);
    }
    if (savedLikes) {
      try {
        setLikedItems(JSON.parse(savedLikes));
      } catch {
        setLikedItems([]);
      }
    } else {
      setLikedItems([]);
    }
    if (savedOrders) {
      try {
        setOrderedItems(JSON.parse(savedOrders));
      } catch {
        setOrderedItems([]);
      }
    } else {
      setOrderedItems([]);
    }

    if (hasAuthToken()) {
      loadRecentVisitsFromBackend();
    }
  }, [userStorageId, loadRecentVisitsFromBackend]);

  const pruneRecentVisitsNow = useCallback(() => {
    setRecentVisits((prev) => pruneStaleRecentVisits(prev));
  }, []);

  useEffect(() => {
    if (!userStorageId) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') pruneRecentVisitsNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    const intervalId = window.setInterval(pruneRecentVisitsNow, 60 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(intervalId);
    };
  }, [userStorageId, pruneRecentVisitsNow]);

  useEffect(() => {
    if (userStorageId) {
      localStorage.setItem(`recent_visits_${userStorageId}`, JSON.stringify(recentVisits));
    }
  }, [recentVisits, userStorageId]);

  useEffect(() => {
    if (userStorageId && likedItems.length >= 0) {
      localStorage.setItem(`liked_items_${userStorageId}`, JSON.stringify(likedItems));
    }
  }, [likedItems, userStorageId]);

  useEffect(() => {
    if (userStorageId && orderedItems.length >= 0) {
      localStorage.setItem(`ordered_items_${userStorageId}`, JSON.stringify(orderedItems));
    }
  }, [orderedItems, userStorageId]);

  const persistVisitToBackend = useCallback(
    async (productId) => {
      if (!user || !hasAuthToken() || !productId) return;
      if (shouldThrottleRecentVisitPost(String(productId))) return;
      try {
        const response = await recentVisitsService.recordVisit(productId);
        if (response && response.success === false) {
          console.warn('Recent visit POST rejected:', response?.error || response?.message);
        }
      } catch (err) {
        console.warn('Recent visit POST failed (check BE route exists):', err?.message || err);
      }
    },
    [user]
  );

  const trackVisit = useCallback(
    (product) => {
      if (!user || !product || !isCatalogProductForDisplay(product)) return;

      const productId = product.id ?? product._id;
      if (productId == null || String(productId).trim() === '') return;
      const id = String(productId);

      const visitItem = {
        id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: getPrimaryProductImageUrl(product),
        category: product.category,
        subcategory: product.subcategory,
        url: product.url,
        isNew: product.isNew,
        isOnSale: product.isOnSale,
        visitedAt: new Date().toISOString(),
        visits: 1,
      };

      setRecentVisits((prevVisits) => {
        const pruned = pruneStaleRecentVisits(prevVisits);
        const existingIndex = pruned.findIndex((item) => String(item.id) === id);

        if (existingIndex >= 0) {
          const updated = [...pruned];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...visitItem,
            visitedAt: new Date().toISOString(),
            visits: (updated[existingIndex].visits || 1) + 1,
          };
          return [
            updated[existingIndex],
            ...updated.slice(0, existingIndex),
            ...updated.slice(existingIndex + 1),
          ];
        }
        return [visitItem, ...pruned.slice(0, 49)];
      });

      persistVisitToBackend(id);
    },
    [user, persistVisitToBackend]
  );

  const trackLike = (product) => {
    if (!user || !product) return;

    const productId = product.id ?? product._id;
    if (productId == null) return;
    const id = String(productId);

    const likeItem = {
      id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: getPrimaryProductImageUrl(product),
      category: product.category,
      subcategory: product.subcategory,
      url: product.url,
      isNew: product.isNew,
      isOnSale: product.isOnSale,
      likedAt: new Date().toISOString(),
      likes: 1,
    };

    setLikedItems((prevLikes) => {
      const existingIndex = prevLikes.findIndex((item) => String(item.id) === id);

      if (existingIndex >= 0) {
        const updated = [...prevLikes];
        updated[existingIndex] = {
          ...updated[existingIndex],
          likedAt: new Date().toISOString(),
          likes: (updated[existingIndex].likes || 1) + 1,
        };
        return updated.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      }
      const newLikes = [likeItem, ...prevLikes];
      return newLikes.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    });
  };

  const trackOrder = (product, quantity = 1) => {
    if (!user || !product) return;

    const productId = product.id ?? product._id;
    if (productId == null) return;
    const id = String(productId);

    const orderItem = {
      id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: getPrimaryProductImageUrl(product),
      category: product.category,
      subcategory: product.subcategory,
      url: product.url,
      isNew: product.isNew,
      isOnSale: product.isOnSale,
      orderedAt: new Date().toISOString(),
      orders: quantity,
      totalOrders: quantity,
    };

    setOrderedItems((prevOrders) => {
      const existingIndex = prevOrders.findIndex((item) => String(item.id) === id);

      if (existingIndex >= 0) {
        const updated = [...prevOrders];
        updated[existingIndex] = {
          ...updated[existingIndex],
          orderedAt: new Date().toISOString(),
          orders: (updated[existingIndex].orders || 0) + quantity,
          totalOrders: (updated[existingIndex].totalOrders || 0) + quantity,
        };
        return updated.sort((a, b) => (b.totalOrders || 0) - (a.totalOrders || 0));
      }
      const newOrders = [orderItem, ...prevOrders];
      return newOrders.sort((a, b) => (b.totalOrders || 0) - (a.totalOrders || 0));
    });
  };

  const getRecentVisits = (limit = 10) => recentVisits.slice(0, limit);

  const getMostLiked = (limit = 10) => likedItems.slice(0, limit);

  const getMostOrdered = (limit = 10) => orderedItems.slice(0, limit);

  const value = {
    recentVisits,
    likedItems,
    orderedItems,
    trackVisit,
    trackLike,
    trackOrder,
    getRecentVisits,
    getMostLiked,
    getMostOrdered,
    loadRecentVisitsFromBackend,
  };

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

export default TrackingContext;
